import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, X, Loader2, FileSpreadsheet, FileText, Archive } from "lucide-react";
import { toast } from "sonner";

// Bar aksi massal: tampil saat ada baris terceklis di tabel modul mana pun.
// onArchive (opsional) menggantikan tombol Hapus dengan "Arsipkan Terpilih" (soft-delete).
export default function BulkActionBar({ count, onClear, onDelete, onArchive, archiveLabel = "Arsipkan Terpilih", onExportExcel, onExportPdf, entity = "data", extraActions = [] }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState("");
  if (!count) return null;

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch {
      toast.error("Sebagian aksi gagal — coba lagi");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/80 px-3 py-2"
      data-testid="bulk-action-bar">
      <span className="text-sm font-semibold text-blue-800" data-testid="bulk-count">{count} {entity} terceklis</span>
      {onExportExcel && (
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run("xls", onExportExcel)}
          className="h-8 gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-export-excel">
          {busy === "xls" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
          Ekspor Excel
        </Button>
      )}
      {onExportPdf && (
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run("pdf", onExportPdf)}
          className="h-8 gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="bulk-export-pdf">
          {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          Ekspor PDF
        </Button>
      )}
      {extraActions.map((a) => (
        <Button key={a.key} size="sm" variant="outline" disabled={!!busy} onClick={() => run(a.key, a.onClick)}
          className={`h-8 gap-1.5 ${a.className || ""}`} data-testid={`bulk-${a.key}`}>
          {busy === a.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : a.icon}
          {a.label}
        </Button>
      ))}
      {onArchive && (
        <Button size="sm" disabled={!!busy} onClick={() => run("arsip", onArchive)}
          className="h-8 gap-1.5 bg-slate-700 hover:bg-slate-800 text-white" data-testid="bulk-archive">
          {busy === "arsip" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
          {archiveLabel}
        </Button>
      )}
      {onDelete && !onArchive && (
        <Button size="sm" disabled={!!busy} onClick={() => setConfirmOpen(true)}
          className="h-8 gap-1.5 bg-rose-600 hover:bg-rose-700 text-white" data-testid="bulk-delete">
          {busy === "del" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          Hapus Terpilih
        </Button>
      )}
      <button type="button" onClick={onClear} className="ml-auto text-blue-600 hover:text-blue-800"
        title="Batalkan pilihan" data-testid="bulk-clear">
        <X className="h-4 w-4" />
      </button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {count} {entity} terpilih?</AlertDialogTitle>
            <AlertDialogDescription>Semua data yang diceklis akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={() => run("del", onDelete)} className="bg-rose-600 hover:bg-rose-700"
              data-testid="bulk-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Hook pilihan baris: Set of ids + toggle + selectAll
export const useRowSelection = () => {
  const [selected, setSelected] = useState(new Set());
  const toggle = (id) => setSelected((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = (ids) => setSelected((p) => (p.size >= ids.length ? new Set() : new Set(ids)));
  const clear = () => setSelected(new Set());
  return { selected, toggle, toggleAll, clear };
};
